/**
 * Read EVERY row a PostgREST list query matches, in pages.
 *
 * WHY: PostgREST caps every response at `max_rows` (1000 — supabase/config.toml
 * [api]; hosted default is the same) and says NOTHING when it cuts a list
 * short: no error, no header the caller checks. A tenant-year `jobs` read
 * returned exactly 1000 rows on prod and every number built from it was wrong
 * (Ready-to-bill hid unbilled work). Any list that can outgrow 1000 rows for
 * one tenant goes through here — never a bare `await supabase.from(...)`.
 *
 * CONTRACT for `buildQuery(selectOpts)`:
 *   - returns a FRESH builder on every call (builders are single-use thenables);
 *   - passes `selectOpts` through to `.select(columns, selectOpts)` so the
 *     first page can ask for the exact total;
 *   - applies a DETERMINISTIC order that ends in a unique column (e.g.
 *     `.order('date', …).order('id')`) — offset pages over a non-unique order
 *     can skip or repeat rows at page boundaries.
 *
 * The first page carries `count: 'exact'`; the rest run in parallel (capped)
 * once the total is known. If the server cap is SMALLER than `pageSize`, the
 * first page's length becomes the step, so a lowered cap cannot truncate.
 * Rows are de-duplicated by `id` (a concurrent insert can shift an offset).
 *
 * @param {(selectOpts: object) => object} buildQuery
 * @param {{ pageSize?: number, concurrency?: number }} [opts]
 * @returns {Promise<{ data: Array|null, error: Error|null, count: number|null }>}
 */
export const FETCH_ALL_PAGE_SIZE = 1000;
const MAX_TOTAL_ROWS = 250000;

export async function fetchAllRows(buildQuery, { pageSize = FETCH_ALL_PAGE_SIZE, concurrency = 4, retried = false } = {}) {
  const first = await buildQuery({ count: 'exact' }).range(0, pageSize - 1);
  if (first.error) return { data: null, error: first.error, count: null };
  const firstRows = first.data || [];
  const total = typeof first.count === 'number' ? first.count : null;
  const step = firstRows.length;

  // Short first page = that was everything (also covers a missing count).
  if (step === 0 || (total != null && step >= total) || (total == null && step < pageSize)) {
    return { data: firstRows, error: null, count: total ?? step };
  }
  if (total == null) return sequentialRest(buildQuery, firstRows, step);
  if (total > MAX_TOTAL_ROWS) {
    return { data: null, error: new Error(`fetchAllRows: ${total} rows exceeds the ${MAX_TOTAL_ROWS} safety limit`), count: total };
  }

  const ranges = [];
  for (let from = step; from < total; from += step) ranges.push([from, Math.min(from + step, total) - 1]);
  const pages = [firstRows];
  for (let i = 0; i < ranges.length; i += concurrency) {
    const batch = ranges.slice(i, i + concurrency);
    // eslint-disable-next-line no-await-in-loop
    const results = await Promise.all(batch.map(([from, to]) => buildQuery({}).range(from, to)));
    const failed = results.find((r) => r.error);
    if (failed) return { data: null, error: failed.error, count: total };
    results.forEach((r) => pages.push(r.data || []));
  }
  const rows = dedupeById(pages.flat());
  // A write between pages can shift a row past a page already read. Fewer
  // rows than the total ⇒ read once more; a second short read is real (a row
  // was deleted mid-read) and is returned as-is.
  if (rows.length < total && !retried) return fetchAllRows(buildQuery, { pageSize, concurrency, retried: true });
  return { data: rows, error: null, count: total };
}

// No total available (count unsupported on this source): walk pages until one
// comes back short.
async function sequentialRest(buildQuery, firstRows, step) {
  const rows = [...firstRows];
  for (let from = step; from < MAX_TOTAL_ROWS; from += step) {
    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await buildQuery({}).range(from, from + step - 1);
    if (error) return { data: null, error, count: null };
    const batch = data || [];
    rows.push(...batch);
    if (batch.length < step) return { data: dedupeById(rows), error: null, count: rows.length };
  }
  return { data: null, error: new Error(`fetchAllRows: exceeded the ${MAX_TOTAL_ROWS} safety limit`), count: null };
}

function dedupeById(rows) {
  const seen = new Set();
  const out = [];
  for (const row of rows) {
    const id = row?.id;
    if (id == null) { out.push(row); continue; }
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(row);
  }
  return out;
}
